// UserDashboard.js
import React, { useState, useEffect } from 'react';
import apiService, { errorHelpers, formatHelpers } from '../../utils/apiService';
import VerificationForm from './user/VerificationForm';
import VerificationHistory from './user/VerificationHistory';
import UserProfile from './user/UserProfile';
import UserStatistics from './user/UserStatistics';

const UserDashboard = ({ activeTab, setActiveTab, user }) => {
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);

    useEffect(() => {
        loadStatistics();
    }, []);

    const loadStatistics = async () => {
        try {
            const response = await apiService.verification.getStatistics();
            setStatistics(response.data.statistics);
        } catch (error) {
            console.error('Failed to load statistics:', error);
            setAlert({
                type: 'error',
                message: 'Failed to load dashboard statistics'
            });
        } finally {
            setLoading(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'verify':
                return <VerificationForm onVerificationComplete={loadStatistics} />;
            case 'history':
                return <VerificationHistory />;
            case 'profile':
                return <UserProfile user={user} />;
            case 'overview':
            default:
                return (
                    <div>
                        <UserStatistics statistics={statistics} loading={loading} />
                        <div className="mt-6">
                            <VerificationForm onVerificationComplete={loadStatistics} compact />
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="user-dashboard">
            {alert && (
                <div className={`alert alert-${alert.type} mb-4`}>
                    {alert.message}
                    <button
                        onClick={() => setAlert(null)}
                        className="ml-4 text-lg font-bold cursor-pointer bg-transparent border-none"
                    >
                        ×
                    </button>
                </div>
            )}

            <div className="dashboard-header">
                <div>
                    <h1 className="dashboard-title">
                        Welcome back, {user.firstname}!
                    </h1>
                    <p className="text-gray-600">
                        Verify your certificates securely and view your verification history.
                    </p>
                </div>

                {statistics && (
                    <div className="user-info">
                        <div className="flex items-center gap-4">
                            <div className="text-center">
                                <div className="text-lg font-semibold text-blue-600">
                                    {statistics.total_verifications}
                                </div>
                                <div className="text-xs text-gray-500">Total Verifications</div>
                            </div>
                            <div className="text-center">
                                <div className="text-lg font-semibold text-green-600">
                                    {statistics.success_rate}%
                                </div>
                                <div className="text-xs text-gray-500">Success Rate</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Quick Action Cards */}
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <QuickActionCard
                        icon="🔍"
                        title="Verify Certificate"
                        description="Verify a new certificate"
                        onClick={() => setActiveTab('verify')}
                        color="blue"
                    />
                    <QuickActionCard
                        icon="📊"
                        title="View History"
                        description="Check your verification history"
                        onClick={() => setActiveTab('history')}
                        color="green"
                    />
                    <QuickActionCard
                        icon="👤"
                        title="Update Profile"
                        description="Manage your account settings"
                        onClick={() => setActiveTab('profile')}
                        color="purple"
                    />
                </div>
            )}

            {/* Tab Content */}
            <div className="tab-content">
                {renderTabContent()}
            </div>
        </div>
    );
};

// Quick Action Card Component
const QuickActionCard = ({ icon, title, description, onClick, color = 'blue' }) => {
    const colorClasses = {
        blue: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50',
        green: 'border-green-200 hover:border-green-400 hover:bg-green-50',
        purple: 'border-purple-200 hover:border-purple-400 hover:bg-purple-50'
    };

    const iconColorClasses = {
        blue: 'text-blue-600',
        green: 'text-green-600',
        purple: 'text-purple-600'
    };

    return (
        <div
            className={`card cursor-pointer transition-all duration-300 ${colorClasses[color]}`}
            onClick={onClick}
        >
            <div className="card-body text-center">
                <div className={`text-3xl mb-3 ${iconColorClasses[color]}`}>
                    {icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{title}</h3>
                <p className="text-gray-600 text-sm">{description}</p>
            </div>
        </div>
    );
};

export default UserDashboard;